import { __assign, __makeTemplateObject, __rest } from "tslib";
import * as React from 'react';
import * as PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { color, spacing } from '@metro-ui/tokens/js';
import { fontStack } from '../utils/fontStack';
import { mq } from '../utils/mediaQuery';
import { responsiveProp, responsivePropTypes, } from '../utils/responsiveProp';
import { Logo } from '../Logo';
import { Layout } from '../Layout';
import { Link, LinkPropTypes } from '../Link';
var headerHeight = {
    small: '3.25rem',
    medium: '4.5rem',
};
var topPositionMap = {
    MetroLogo: {
        small: '17px',
        medium: '24px',
    },
    MetroLogoTrInverted: {
        small: '12px',
        medium: '17px',
    },
    MetroLogoCnInverted: {
        small: '10px',
        medium: '14px',
    },
    MakroLogo: {
        small: '12px',
        medium: '16.43px',
    },
    MetroLogoCorporateInverted: {
        small: '17px',
        medium: '24px',
    },
};
var isSupportedLogo = function (variant) {
    return variant.displayName &&
        Object.keys(topPositionMap).indexOf(variant.displayName) > -1;
};
var HeaderLayout = styled(Layout, {
    shouldForwardProp: function (prop) {
        return prop !== 'shouldEscapePadding' && prop !== 'maxWidth';
    },
})(function (_a) {
    var maxWidth = _a.maxWidth, shouldEscapePadding = _a.shouldEscapePadding;
    return css(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n    height: 100%;\n    ", "\n    margin: 0 auto;\n\n    ", "\n  "], ["\n    height: 100%;\n    ", "\n    margin: 0 auto;\n\n    ", "\n  "])), maxWidth && css(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n      max-width: ", "px;\n    "], ["\n      max-width: ", "px;\n    "])), maxWidth), responsiveProp(shouldEscapePadding, false, function (val) {
        return val
            ? "\n        padding: 0 0 0 " + spacing.small + ";\n        " + mq('l') + " {\n          padding: 0 0 0 " + spacing.large + ";\n        }\n      "
            : "\n      padding: 0 " + spacing.small + ";\n      " + mq('l') + " {\n        padding: 0 " + spacing.large + ";\n      }\n    ";
    }));
});
var LogoContainer = styled.div(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n  display: inline-flex;\n  flex-shrink: 0;\n  margin-right: ", ";\n  height: ", ";\n\n  ", " {\n    margin-right: ", ";\n    height: ", ";\n  }\n"], ["\n  display: inline-flex;\n  flex-shrink: 0;\n  margin-right: ", ";\n  height: ", ";\n\n  ", " {\n    margin-right: ", ";\n    height: ", ";\n  }\n"])), spacing.small, headerHeight.small, mq('l'), spacing.large, headerHeight.medium);
var HeaderLogo = styled(Logo)(templateObject_5 || (templateObject_5 = __makeTemplateObject(["\n  position: relative;\n  ", "\n"], ["\n  position: relative;\n  ", "\n"])), function (_a) {
    var variant = _a.variant;
    var lv = variant.displayName;
    return css(templateObject_4 || (templateObject_4 = __makeTemplateObject(["\n      top: ", ";\n      ", " {\n        top: ", ";\n      }\n    "], ["\n      top: ", ";\n      ", " {\n        top: ", ";\n      }\n    "])), topPositionMap[lv].small, mq('l'), topPositionMap[lv].medium);
});
var HeaderLink = styled(Link)(templateObject_6 || (templateObject_6 = __makeTemplateObject(["\n  height: 100%;\n  align-items: flex-start;\n"], ["\n  height: 100%;\n  align-items: flex-start;\n"])));
var HeaderPosition = styled(function (_a) {
    var className = _a.className, children = _a.children;
    return (React.createElement("div", { className: className }, children));
})(function (_a) {
    var _b = _a.top, top = _b === void 0 ? 0 : _b, _c = _a.height, height = _c === void 0 ? { xs: 3.25, l: 4.5 } : _c;
    return css(templateObject_7 || (templateObject_7 = __makeTemplateObject(["\n    position: relative;\n    width: 100%;\n    top: ", "px;\n\n    ", "\n  "], ["\n    position: relative;\n    width: 100%;\n    top: ", "px;\n\n    ", "\n  "])), top, responsiveProp(height, 3.25, function (val) {
        return val ? "height: " + val + "rem;" : undefined;
    }));
});
export var Header = styled(function (_a) {
    var Variant = _a.Logo, shouldEscapePadding = _a.shouldEscapePadding, shouldWrap = _a.shouldWrap, position = _a.position, top = _a.top, height = _a.height, _b = _a.maxWidth, maxWidth = _b === void 0 ? 1920 : _b, linkAttributes = _a.linkAttributes, logoDimensions = _a.logoDimensions, className = _a.className, children = _a.children, htmlAttributes = __rest(_a, ["Logo", "shouldEscapePadding", "shouldWrap", "position", "top", "height", "maxWidth", "linkAttributes", "logoDimensions", "className", "children"]);
    var ConfiguredLogo = isSupportedLogo(Variant) ? (React.createElement(HeaderLogo, { variant: Variant, size: { xs: 'small', l: 'medium' } })) : (React.createElement(Variant, { dimensions: logoDimensions, style: { alignSelf: 'center' } }));
    var ConfiguredHeader = (React.createElement("header", __assign({}, htmlAttributes, { className: className }),
        React.createElement(HeaderLayout, { shouldEscapePadding: shouldEscapePadding, shouldWrap: shouldWrap, maxWidth: maxWidth },
            React.createElement(LogoContainer, null, linkAttributes ? (React.createElement(HeaderLink, __assign({}, linkAttributes), ConfiguredLogo)) : (ConfiguredLogo)),
            children)));
    return position === 'fixed' ? (React.createElement(HeaderPosition, { height: height, top: top }, ConfiguredHeader)) : (ConfiguredHeader);
})(function (_a) {
    var _b = _a.position, position = _b === void 0 ? 'relative' : _b, _c = _a.top, top = _c === void 0 ? 0 : _c;
    return css(templateObject_8 || (templateObject_8 = __makeTemplateObject(["\n    ", "\n    position: ", ";\n    top: ", "px;\n    left: 0;\n    background-color: ", ";\n    color: ", ";\n    width: 100%;\n    min-height: ", ";\n\n    ", " {\n      min-height: ", ";\n    }\n  "], ["\n    ", "\n    position: ", ";\n    top: ", "px;\n    left: 0;\n    background-color: ", ";\n    color: ", ";\n    width: 100%;\n    min-height: ", ";\n\n    ", " {\n      min-height: ", ";\n    }\n  "])), fontStack, position, top, color.brand.primary.default, color.inverted, headerHeight.small, mq('l'), headerHeight.medium);
});
Header.propTypes = {
    Logo: PropTypes.func.isRequired,
    shouldEscapePadding: responsivePropTypes(PropTypes.bool),
    shouldWrap: responsivePropTypes(PropTypes.bool),
    position: PropTypes.oneOf(['relative', 'fixed']),
    top: PropTypes.number,
    height: responsivePropTypes(PropTypes.number),
    maxWidth: PropTypes.oneOf([1920, 1440, null]),
    // @ts-ignore
    linkAttributes: PropTypes.shape(LinkPropTypes),
    logoDimensions: responsivePropTypes(PropTypes.shape({
        width: PropTypes.number.isRequired,
        height: PropTypes.number.isRequired,
    })),
    className: PropTypes.string,
    children: PropTypes.node,
};
var templateObject_1, templateObject_2, templateObject_3, templateObject_4, templateObject_5, templateObject_6, templateObject_7, templateObject_8;
//# sourceMappingURL=Header.js.map